import { useEffect, useState } from 'react'

const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
  const [pageInput, setPageInput] = useState(String(currentPage))

  useEffect(() => { setPageInput(String(currentPage)) }, [currentPage])

  // An empty result set still has one (empty) page as far as the paginator is
  // concerned. Clamping to a raw totalPages of 0 produced page 0, and a page 0
  // makes `start` negative — hence the "Showing -9-0 out of 0" readout.
  const lastPage = Math.max(1, totalPages)
  const clampPage = (page) => Math.min(Math.max(1, page), lastPage)

  const goToPage = (page) => {
    onPageChange(clampPage(page))
  }

  // The input is resynced to the page actually navigated to on every commit,
  // not just when currentPage changes: typing 600 with 510 pages clamps to 510,
  // and if the user was already on 510 the effect above never fires, leaving the
  // box reading 600 while the table shows page 510.
  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10)
    const target = Number.isNaN(parsed) ? currentPage : clampPage(parsed)
    setPageInput(String(target))
    if (target !== currentPage) onPageChange(target)
  }

  const start = (currentPage - 1) * itemsPerPage
  const firstShown = totalItems === 0 ? 0 : start + 1
  const lastShown = Math.min(start + itemsPerPage, totalItems)

  return (
    <div className="flex items-center justify-between px-4 py-2.5 shrink-0 flex-wrap gap-2 bg-[#f9fafa]">
      <span className="text-sm text-[#272d37]">
        Showing <strong>{firstShown}-{lastShown}</strong> out of <strong>{totalItems}</strong>
      </span>

      <div className="flex items-center gap-1">
        <button className="w-7 h-7 border border-[#dfe4ed] rounded-md bg-white cursor-pointer flex items-center justify-center p-0 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => goToPage(1)} disabled={currentPage <= 1}>
          <span className="material-icons text-base text-[#616a79]">first_page</span>
        </button>
        <button className="w-7 h-7 border border-[#dfe4ed] rounded-md bg-white cursor-pointer flex items-center justify-center p-0 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
          <span className="material-icons text-base text-[#616a79]">chevron_left</span>
        </button>
        <input
          className="w-9 h-7 border border-[#dfe4ed] rounded-md text-center text-sm text-[#272d37] outline-none"
          inputMode="numeric"
          aria-label={`Page number, 1 to ${lastPage}`}
          value={pageInput}
          onChange={e => setPageInput(e.target.value)}
          onBlur={commitPageInput}
          onKeyDown={e => e.key === 'Enter' && commitPageInput()}
        />
        <span className="text-sm text-[#616a79]">of {totalPages} pages</span>
        <button className="w-7 h-7 border border-[#dfe4ed] rounded-md bg-white cursor-pointer flex items-center justify-center p-0 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= lastPage}>
          <span className="material-icons text-base text-[#616a79]">chevron_right</span>
        </button>
        <button className="w-7 h-7 border border-[#dfe4ed] rounded-md bg-white cursor-pointer flex items-center justify-center p-0 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => goToPage(totalPages)} disabled={currentPage >= lastPage}>
          <span className="material-icons text-base text-[#616a79]">last_page</span>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-[#616a79]">Items per page</span>
        <select
          className="h-7 border border-[#dfe4ed] rounded-md text-sm text-[#272d37] px-1.5 cursor-pointer outline-none"
          value={itemsPerPage}
          onChange={e => { onItemsPerPageChange(Number(e.target.value)); onPageChange(1) }}
        >
          {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  )
}

export default Pagination
